import React, { useCallback, useState } from 'react'
import DataGridV2 from '../../components/DataGridV2';
import { useTranslation } from 'react-i18next';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useSelector } from 'react-redux';
import { INITIAL_ROW_SELECTION_MODEL } from '../../components/DataGridV2/constants';
import { formatDate, getLocalStorageItem, setLocalStorageItem } from '../../_helpers';
import { apps, LOCAL_STORAGE_GRID_KEYS } from '../../_constants';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Call, Cancel, Close } from '@mui/icons-material';
import { grey } from '@mui/material/colors';
import Button from '../../components/Button';
import { getBuyerRepairRequestResolverColumns } from './buyerRepairRequestResolverUtils';
const { webApiUrl } = window.appConfig;

const BuyerRepairRequestResolversGrid = ({ resolvers }) => {
  const { t, i18n } = useTranslation()
  const apiRef = useGridApiRef();
  const theme = useTheme()
  const classes = useStyles();
  const app = useSelector((state) => state.app);
  const keyPrefix = app === apps.aftercare ? "AFTERCARE" : "GENERAL";
  const [rowSelectionModel, setRowSelectionModel] = useState(INITIAL_ROW_SELECTION_MODEL);
  const [paginationModel, setPaginationModel] = useState(() => getLocalStorageItem(
    LOCAL_STORAGE_GRID_KEYS.BUYER_REPAIR_REQUEST_RESOLVERS_PAGINATION[keyPrefix], undefined
  ))
  const [isAppointmentCancelDialogOpen, setIsAppointmentCancelDialogOpen] = useState(false)
  const [selectedResolver, setSelectedResolver] = useState(null);

  const handleAppointmentCancelDialogOpen = (resolver) => {
    setSelectedResolver(resolver);
    setIsAppointmentCancelDialogOpen(true);
  };

  const handleAppointmentCancelDialogClose = () => {
    setIsAppointmentCancelDialogOpen(false)
  }
  const rows = resolvers.map(item => ({
    ...item,
    id: item.resolverId,
  }));

  const columns = getBuyerRepairRequestResolverColumns({ t, keyPrefix, i18n, apiRef, theme, classes, handleAppointmentCancelDialogOpen })

  const onPaginationModelChange = useCallback((newPaginationModel) => {
    setPaginationModel(newPaginationModel)
    setLocalStorageItem(
      LOCAL_STORAGE_GRID_KEYS.BUYER_REPAIR_REQUEST_RESOLVERS_PAGINATION[keyPrefix],
      newPaginationModel
    );
  }, [keyPrefix])

  const getBuyerResolversForPrintAndDownload = ({ isAllSelected = true, idsToExport = [] }) => {
    const userSelectedRows = isAllSelected ? rows : idsToExport.map(id => rows?.find(item => item?.resolverId === id));
    const results = userSelectedRows?.map((item) => {
      let result = {}
      Object.keys(item).forEach((key) => {
        let value = item[key];
        const isDateField = columns.some(x => x.field === key && x.type === 'date');
        const isDateTimeField = columns.some(x => x.field === key && x.type === 'dateTime');

        if (isDateTimeField) {
          value = item[key] ? formatDate(new Date(item[key]), true) : "";
        } else if (isDateField) {
          value = item[key] ? formatDate(new Date(item[key])) : "";
        }

        result[key] = value;
      });
      return result
    })
    return results;
  };

  return (
    <Box className={classes.mainContainer}>
      <DataGridV2
        localSearchIdentifier={LOCAL_STORAGE_GRID_KEYS.BUYER_REPAIR_REQUEST_RESOLVERS_SEARCH_TEXT[keyPrefix]}
        localColumnFilterIdentifier={LOCAL_STORAGE_GRID_KEYS.BUYER_REPAIR_REQUEST_RESOLVERS_COLUMN_FILTER[keyPrefix]}
        localColumnSortIdentifier={LOCAL_STORAGE_GRID_KEYS.BUYER_REPAIR_REQUEST_RESOLVERS_COLUMN_SORT[keyPrefix]}
        localColumnOrderIdentifier={LOCAL_STORAGE_GRID_KEYS.BUYER_REPAIR_REQUEST_RESOLVERS_COLUMN_ORDER[keyPrefix]}
        localColumnWidthIdentifier={LOCAL_STORAGE_GRID_KEYS.BUYER_REPAIR_REQUEST_RESOLVERS_COLUMN_WIDTH[keyPrefix]}
        localColumnVisibilityIdentifier={LOCAL_STORAGE_GRID_KEYS.BUYER_REPAIR_REQUEST_RESOLVERS_COLUMN_VISIBILITY[keyPrefix]}
        apiRef={apiRef}
        checkboxSelection
        rows={rows}
        columns={columns}
        pagination
        filterMode="client"
        {...(!!paginationModel && { paginationModel })}
        onPaginationModelChange={onPaginationModelChange}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={(newRowSelectionModel) => {
          setRowSelectionModel(newRowSelectionModel);
        }}
        toolbarProps={{
          selectedRows: rowSelectionModel,
          getData: getBuyerResolversForPrintAndDownload,
          title: t('general.resolver')
        }}
      />

      {selectedResolver && <Dialog
        open={isAppointmentCancelDialogOpen}
        onClose={handleAppointmentCancelDialogClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 1.5, bgcolor: grey[200] }} id="customized-dialog-title">
          <Typography>
            <Cancel color="primary" sx={{ fontSize: 30 }} /> &nbsp;
            {t("repairRequest.cancelOrRescheduleAppointment.title")}
          </Typography>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => handleAppointmentCancelDialogClose()}
          sx={theme => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500]
          })}
        >
          <Close sx={{ color: grey[900] }} />
        </IconButton>
        <DialogContent>
          <Stack spacing={2}>
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                fontWeight: 400
              }}>
              {t("repairRequest.cancelOrRescheduleAppointment.subTitle")}
            </Typography>
            <Stack
              direction="row"
              sx={{
                rowGap: 1,
                p: 0.5,
                justifyContent: "center",
                alignItems: "center",
                bgcolor: grey[100]
              }}>
              <Box
                component="img"
                src={`${webApiUrl}api/organisation/GetOrganisationLogo/${selectedResolver.organisatonId}`}
                class={classes.modalContentIcon}
              />
              <Stack sx={{
                columnGap: 1
              }}>
                <Typography variant="body2" sx={{
                  color: grey[900]
                }}>{selectedResolver?.name}</Typography>
                {
                  selectedResolver?.telephone && <Typography variant="body2" sx={{ color: "primary.main" }} >
                    <Call fontSize="small" sx={{ pr: 0.5 }} />
                    {selectedResolver.telephone}
                  </Typography>
                }

              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleAppointmentCancelDialogClose}>
            {t("general.cancel")}
          </Button>
        </DialogActions>
      </Dialog>
      }
    </Box>

  )
}
const useStyles = makeStyles((theme) => ({
  mainContainer: {
    height: "100%",
    width: "100%",
    overflow: "hidden ",
    padding: 0,
  },
  lineThrough: {
    textDecoration: 'line-through'
  },
  modalContentIcon: {
    display: "block",
    maxWidth: "150px",
    maxHeight: "40px",
    height: "100%",
    marginRight: "1rem",
    objectFit: "contain"
  },
}));
export default BuyerRepairRequestResolversGrid